import React, { useState, useEffect, useContext } from 'react';
import { useOutletContext, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import DashboardPulse from '../components/DashboardPulse';
import TutorialModal from '../components/TutorialModal';
import { hasSeenTutorial } from '../utils/tutorialData';
import { getRoleLabel } from '../utils/permissions';
import {
  UserIcon as User,
  TeamIcon as Users,
  CalendarIcon as Calendar,
  BlogIcon as FileText,
  MessageIcon as MessageSquare,
  SettingsIcon as Settings,
  NotificationIcon as Bell,
  AddUserIcon as UserPlus,
  ClipboardIcon as ClipboardList,
  GlobeIcon as Globe,
  AlertCircleIcon as AlertCircle,
  ArrowRightIcon as ArrowRight,
  InboxIcon,
  CheckboxIcon as TaskIcon,
  PlusIcon,
  CheckIcon,
  LocationIcon as MapPin,
  ClockIcon,
  KeyIcon,
  SparklesIcon
} from '../components/Icons';

// Premium glassmorphic navigation card
const QuickLinkCard = ({ to, icon: Icon, title, description, colorClass = 'primary' }) => {
  const colorMap = {
    primary: {
      iconBg: 'bg-primary/10 text-primary',
      border: 'hover:border-primary/50'
    },
    secondary: {
      iconBg: 'bg-secondary/10 text-secondary',
      border: 'hover:border-secondary/50'
    },
    tertiary: {
      iconBg: 'bg-tertiary/10 text-tertiary',
      border: 'hover:border-tertiary/50'
    },
    error: {
      iconBg: 'bg-error/10 text-error',
      border: 'hover:border-error/50'
    }
  };

  const colors = colorMap[colorClass] || colorMap.primary;

  return (
    <Link
      to={to}
      className={`bg-surface-containerLow/50 backdrop-blur-md rounded-3xl p-6 flex flex-col gap-4 group transition-all duration-medium2 ease-emphasized border border-outlineVariant/10 ${colors.border} hover:shadow-elevation-1`}
    >
      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm group-hover:scale-110 group-hover:rotate-3 transition-transform duration-medium2 ${colors.iconBg}`}>
        <Icon size={24} />
      </div>
      <div>
        <h3 className="text-title-medium font-bold text-surface-on mb-1 flex items-center gap-2">
          {title}
          <ArrowRight size={16} className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-primary" />
        </h3>
        <p className="text-body-small text-surface-onVariant leading-relaxed opacity-80">
          {description}
        </p>
      </div>
    </Link>
  );
};

// Consolidated alerts and notifications block
const SystemAlertsWidget = ({ profile, resendLoading, resendMessage, handleResendVerification }) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  const alerts = [];

  if (!profile.isApproved) {
    alerts.push({
      id: 'approved',
      title: 'Warten auf Freischaltung',
      description: 'Ihr Konto wurde noch nicht von einem Administrator freigeschaltet. Einige Funktionen sind eingeschränkt.',
      colorClass: 'bg-error-container/40 text-error-onContainer border-error/20',
      iconClass: 'bg-error text-error-on',
    });
  }

  if (!profile.isEmailVerified) {
    alerts.push({
      id: 'verified',
      title: 'E-Mail-Verifizierung ausstehend',
      description: 'Bitte bestätigen Sie Ihre E-Mail-Adresse, um alle Funktionen nutzen zu können.',
      colorClass: 'bg-secondary-container/45 text-secondary-onContainer border-secondary/20',
      iconClass: 'bg-secondary text-secondary-on',
      action: (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            onClick={handleResendVerification}
            disabled={resendLoading}
            className="md3-btn-filled !bg-secondary !text-secondary-on shadow-sm text-xs py-1.5 px-4 h-auto rounded-xl"
          >
            {resendLoading ? 'Wird gesendet...' : 'Bestätigung erneut senden'}
          </button>
          {resendMessage && <span className="text-xs font-semibold">{resendMessage}</span>}
        </div>
      )
    });
  }

  const isLeader = profile.role === 'admin' || profile.role === 'gruppenleiter';
  const hasPhoto = profile.userData?.profilePhoto?.url || 
                    (profile.userData?.photos && profile.userData.photos.length > 0) || 
                    profile.userData?.avatarConfig?.modelUrl;

  if (isLeader && !hasPhoto) {
    alerts.push({
      id: 'photo',
      title: 'Profilbild fehlt',
      description: 'Als Teammitglied sollten Sie ein Profilbild oder einen 3D-Avatar hinterlegen, um auf der Team-Seite sichtbar zu sein.',
      colorClass: 'bg-tertiary-container/40 text-tertiary-onContainer border-tertiary/20',
      iconClass: 'bg-tertiary text-tertiary-on',
      action: (
        <div className="mt-3">
          <Link
            to="profile"
            className="md3-btn-filled !bg-tertiary !text-tertiary-on shadow-sm text-xs py-1.5 px-4 h-auto rounded-xl inline-block"
          >
            Profil vervollständigen
          </Link>
        </div>
      )
    });
  }

  if (alerts.length === 0 || isDismissed) return null;

  return (
    <div className="md3-card bg-surface-containerLow border border-outlineVariant/15 p-5 mb-8 transition-all animate-scale-in">
      <div className="flex items-center justify-between gap-4 pb-3 border-b border-outlineVariant/20 mb-4">
        <div className="flex items-center gap-2">
          <AlertCircle size={20} className="text-amber-500" />
          <h4 className="text-title-medium font-bold text-surface-on">System-Status & Hinweise ({alerts.length})</h4>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-[12px] font-bold text-primary hover:bg-primary/5 px-2.5 py-1.5 rounded-xl cursor-pointer transition-colors"
          >
            {isExpanded ? 'Einklappen' : 'Ausklappen'}
          </button>
          <button 
            onClick={() => setIsDismissed(true)}
            className="text-[12px] font-bold text-error hover:bg-error/5 px-2.5 py-1.5 rounded-xl cursor-pointer transition-colors"
          >
            Ausblenden
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="space-y-3">
          {alerts.map((alert) => (
            <div key={alert.id} className={`flex items-start gap-4 p-4 rounded-2xl border ${alert.colorClass}`}>
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${alert.iconClass}`}>
                <AlertCircle size={18} />
              </div>
              <div className="flex-grow min-w-0">
                <h5 className="text-[14px] font-bold tracking-tight mb-0.5">{alert.title}</h5>
                <p className="text-[12px] opacity-90 leading-relaxed">{alert.description}</p>
                {alert.action}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// Setup Checklist Banner (admin only)
// ============================================================
const STATUS_STYLE = {
  ok:       { bg: 'bg-green-500/10 border-green-500/20', text: 'text-green-700 dark:text-green-400', badge: 'bg-green-500 text-white', label: 'OK' },
  missing:  { bg: 'bg-error/10 border-error/20',         text: 'text-error',                        badge: 'bg-error text-white',       label: 'Fehlt' },
  partial:  { bg: 'bg-amber-500/10 border-amber-500/20', text: 'text-amber-700 dark:text-amber-400',badge: 'bg-amber-500 text-white',   label: 'Unvollständig' },
  disabled: { bg: 'bg-surface-containerHigh/60 border-outlineVariant/20', text: 'text-surface-onVariant', badge: 'bg-surface-containerHighest text-surface-onVariant', label: 'Deaktiviert' },
};

const SetupChecklistBanner = ({ user, API_URL }) => {
  const [items, setItems] = useState([]);
  const [allDone, setAllDone] = useState(true);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!user?.token) return;
    const fetchChecklist = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/setup/checklist`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        setItems(data.items || []);
        setAllDone(data.allRequiredDone === true);
      } catch (err) {
        console.warn('Setup checklist unavailable:', err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchChecklist();
  }, [user, API_URL]);

  if (loading || allDone) return null;

  const missingRequired = items.filter(i => i.required && i.status === 'missing');
  const hasIssues = items.some(i => i.status === 'missing' || i.status === 'partial');
  if (!hasIssues) return null;

  return (
    <div className="rounded-3xl border border-error/20 bg-error/5 p-5 mb-8 animate-fade-in-up shadow-elevation-1">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-error/15 text-error flex items-center justify-center shrink-0">
            <AlertCircle size={22} />
          </div>
          <div>
            <h4 className="text-title-medium font-bold text-surface-on">
              {missingRequired.length > 0 
                ? `${missingRequired.length} Pflicht-Einstellung${missingRequired.length > 1 ? 'en' : ''} fehlen`
                : 'Einrichtung unvollständig'}
            </h4>
            <p className="text-body-small text-surface-onVariant mt-0.5">
              {missingRequired.length > 0
                ? 'Das System kann ohne diese Konfigurationen nicht vollständig starten.'
                : 'Einige optionale Integrationen sind noch nicht eingerichtet.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/admin-tools/integrations"
            className="text-xs font-bold px-4 py-2 rounded-xl bg-error text-white hover:bg-error/90 transition-colors flex items-center gap-1.5"
          >
            Jetzt einrichten <ArrowRight size={14} />
          </Link>
          <button
            onClick={() => setCollapsed(c => !c)}
            className="text-xs text-surface-onVariant hover:text-surface-on px-2.5 py-2 rounded-xl hover:bg-surface-containerHigh transition-colors"
          >
            {collapsed ? 'Details' : 'Einklappen'}
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {items.map(item => {
            const s = STATUS_STYLE[item.status] || STATUS_STYLE.missing;
            return (
              <div key={item.id} className={`flex items-start gap-3 p-4 rounded-2xl border ${s.bg}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-bold text-surface-on">{item.label}</span>
                    {item.required && item.status !== 'ok' && item.status !== 'disabled' && (
                      <span className="text-[10px] font-bold bg-error/15 text-error px-1.5 py-0.5 rounded-full">Pflicht</span>
                    )}
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${s.badge}`}>
                      {s.label}
                    </span>
                  </div>
                  <p className="text-[11px] text-surface-onVariant mt-0.5 leading-relaxed">{item.description}</p>
                  {item.statusDetail && (
                    <p className="text-[11px] font-semibold text-error/80 mt-0.5">{item.statusDetail}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Quick Actions Menu Bar
const QuickActionsDock = ({ role, isApproved }) => {
  const actions = [];
  
  if (isApproved && (role === 'admin' || role === 'gruppenleiter')) {
    actions.push({
      label: 'Neues Event',
      to: 'events/edit/new',
      icon: PlusIcon,
      color: 'bg-primary/10 text-primary hover:bg-primary/20',
    });
    actions.push({
      label: 'Neuer Blogpost',
      to: 'blog/new',
      icon: FileText,
      color: 'bg-tertiary/10 text-tertiary hover:bg-tertiary/20',
    });
    actions.push({
      label: 'Aufgabe erstellen',
      to: 'tasks',
      icon: TaskIcon,
      color: 'bg-secondary/10 text-secondary hover:bg-secondary/20',
    });
  } else if (role === 'eltern') {
    actions.push({
      label: 'Kind anmelden',
      to: 'my-children',
      icon: Users,
      color: 'bg-tertiary/10 text-tertiary hover:bg-tertiary/20',
    });
  } else if (role === 'kind') {
    actions.push({
      label: 'Blog schreiben',
      to: 'blog',
      icon: FileText,
      color: 'bg-tertiary/10 text-tertiary hover:bg-tertiary/20',
    });
  }

  if (actions.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-3 mb-8">
      <span className="text-[11px] font-bold text-surface-onVariant/60 uppercase tracking-widest mr-1">Schnellzugriff:</span>
      {actions.map((act, idx) => (
        <Link 
          key={idx} 
          to={act.to}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-[13px] font-bold transition-all duration-200 ${act.color} active:scale-95`}
        >
          <act.icon size={15} />
          {act.label}
        </Link>
      ))}
    </div>
  );
};

// Rich Event Preview Card
const NextEventsWidget = ({ events, loading }) => {
  if (loading) {
    return (
      <div className="bg-surface-containerLow rounded-3xl p-6 border border-outlineVariant/10 animate-pulse h-48" />
    );
  }

  if (!events || events.length === 0) return null;

  if (events.length === 1) {
    const event = events[0];
    const imageUrl = event.pageContent?.hero?.imageUrl;
    
    return (
      <div className="relative overflow-hidden rounded-3xl bg-surface-containerLow border border-outlineVariant/10 shadow-elevation-1 flex flex-col group min-h-[200px] mb-8">
        {imageUrl ? (
          <>
            <img 
              src={imageUrl} 
              alt={event.title} 
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/15 to-tertiary/15" />
        )}
        
        <div className="relative mt-auto p-6 md:p-8 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="space-y-2.5">
            <span className="inline-block text-[9px] font-bold uppercase tracking-widest bg-primary text-primary-on px-2.5 py-1 rounded-full shadow-sm">
              Nächstes Event
            </span>
            <h3 className={`text-headline-small md:text-headline-medium font-bold leading-tight ${imageUrl ? 'text-white' : 'text-surface-on'}`}>
              {event.title}
            </h3>
            <div className={`flex flex-wrap gap-4 text-[13px] font-medium ${imageUrl ? 'text-white/80' : 'text-surface-onVariant'}`}>
              <span className="flex items-center gap-1.5">
                <Calendar size={15} />
                {new Date(event.date).toLocaleDateString('de-DE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
              {event.startTime && (
                <span className="flex items-center gap-1.5">
                  <ClockIcon size={15} />
                  {event.startTime} Uhr
                </span>
              )}
              {event.location && (
                <span className="flex items-center gap-1.5">
                  <MapPin size={15} />
                  {event.location}
                </span>
              )}
            </div>
          </div>
          
          <Link 
            to={event.isRegistrationEnabled ? `my-registrations` : `events`} 
            className="md3-btn-filled shrink-0 !bg-primary text-primary-on hover:shadow-elevation-2 transition-all self-start md:self-end px-5 py-3 rounded-2xl flex items-center gap-2"
          >
            {event.isRegistrationEnabled ? 'Anmeldung verwalten' : 'Event ansehen'}
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  // Multiple events on the next date
  return (
    <div className="bg-surface-containerLow/40 backdrop-blur-md rounded-3xl p-6 border border-outlineVariant/10 shadow-elevation-1 mb-8">
      <div className="flex items-center justify-between mb-4 pb-2 border-b border-outlineVariant/15">
        <span className="text-[10px] font-black uppercase tracking-widest text-primary bg-primary/10 px-2.5 py-1 rounded-full">
          {events.length} Parallele Aktionen am {new Date(events[0].date).toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {events.map((event) => {
          return (
            <div 
              key={event._id}
              className="relative overflow-hidden rounded-2xl bg-surface/50 border border-outlineVariant/15 p-5 flex flex-col justify-between min-h-[160px] group hover:bg-surface/80 hover:border-primary/30 transition-all duration-300"
            >
              <div className="space-y-2">
                <div className="flex justify-between items-start">
                  <span className="text-[9px] font-bold uppercase tracking-wider bg-secondary/15 text-secondary px-2 py-0.5 rounded-md">
                    {event.category}
                  </span>
                  {event.startTime && (
                    <span className="text-[11px] font-semibold text-surface-onVariant/70 flex items-center gap-1">
                      <ClockIcon size={12} />
                      {event.startTime} Uhr
                    </span>
                  )}
                </div>
                <h4 className="text-title-medium font-bold text-surface-on leading-snug line-clamp-2">
                  {event.title}
                </h4>
                {event.location && (
                  <p className="text-[11px] text-surface-onVariant/80 flex items-center gap-1">
                    <MapPin size={12} />
                    {event.location}
                  </p>
                )}
              </div>
              
              <div className="mt-4 pt-3 border-t border-outlineVariant/10 flex justify-between items-center">
                <Link 
                  to={event.isRegistrationEnabled ? `my-registrations` : `events`} 
                  className="text-primary hover:text-primary-hover font-bold text-[13px] flex items-center gap-1 group/btn"
                >
                  {event.isRegistrationEnabled ? 'Anmelden' : 'Ansehen'}
                  <ArrowRight size={14} className="group-hover/btn:translate-x-0.5 transition-transform" />
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Interactive tasks checklist widget
const TaskListWidget = ({ tasks, loading, onToggleTask, navigate }) => {
  if (loading) {
    return (
      <div className="bg-surface-containerLow rounded-3xl p-6 border border-outlineVariant/10 animate-pulse">
        <div className="h-6 w-32 bg-surface-onVariant/10 rounded mb-4" />
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-10 bg-surface-onVariant/10 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-containerLow/70 backdrop-blur-md rounded-3xl p-5 border border-outlineVariant/10 shadow-elevation-1 flex flex-col gap-4">
      <div className="flex items-center justify-between pb-2 border-b border-outlineVariant/20">
        <h3 className="text-title-medium font-bold text-surface-on flex items-center gap-2">
          <TaskIcon size={20} className="text-primary" /> Meine Aufgaben
        </h3>
        <span className="text-[10px] font-bold uppercase tracking-wider bg-primary/10 text-primary px-2.5 py-1 rounded-full">
          {tasks.length} offen
        </span>
      </div>
      
      {tasks.length === 0 ? (
        <div className="py-6 text-center text-surface-onVariant opacity-65 flex flex-col items-center gap-2">
          <CheckIcon size={32} className="text-green-500 bg-green-500/10 p-2 rounded-full" />
          <p className="text-body-medium font-bold">Keine offenen Aufgaben</p>
          <p className="text-[11px]">Gut Pfad! Genieße deinen Tag.</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1 custom-scrollbar">
          {tasks.map((task) => (
            <div 
              key={task._id} 
              className={`flex items-start gap-3 p-3 rounded-2xl bg-surface/40 hover:bg-surface/80 border border-outlineVariant/10 transition-all ${
                task.isDoneChanging ? 'opacity-50 scale-95 transition-all duration-500' : ''
              }`}
            >
              <button 
                onClick={() => onToggleTask(task._id)}
                disabled={task.isDoneChanging}
                className="mt-0.5 w-5 h-5 rounded-md border-2 border-outline hover:border-primary flex items-center justify-center shrink-0 transition-colors bg-transparent cursor-pointer"
              >
                {task.status === 'done' && <CheckIcon style={{ fontSize: 13 }} className="text-primary font-black" />}
              </button>
              <div className="flex-1 min-w-0">
                <p className={`text-[13px] font-bold leading-snug text-surface-on truncate ${
                  task.status === 'done' ? 'line-through opacity-55' : ''
                }`}>
                  {task.title}
                </p>
                {task.deadline && (
                  <p className="text-[10px] text-surface-onVariant mt-0.5 opacity-80">
                    Bis {new Date(task.deadline).toLocaleDateString('de-DE')}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      <button 
        onClick={() => navigate('/tasks')} 
        className="md3-btn-tonal w-full text-label-medium py-3 rounded-2xl justify-center text-primary font-bold transition-all"
      >
        Aufgaben-Board öffnen
      </button>
    </div>
  );
};

const Dashboard = () => {
  const { profile } = useOutletContext();
  const { API_URL, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [showTutorial, setShowTutorial] = useState(!hasSeenTutorial('dashboard'));

  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [orgSettings, setOrgSettings] = useState(null);

  // Fetch interactive widget data
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user?.token) return;
      
      // Fetch Settings for branding / subtitles
      try {
        const { data } = await axios.get(`${API_URL}/settings/homepage`);
        if (data?.organization) {
          setOrgSettings(data.organization);
        }
      } catch (err) {
        console.error("Error fetching homepage settings for dashboard:", err);
      }

      // Fetch Tasks
      try {
        setLoadingTasks(true);
        const config = { headers: { Authorization: `Bearer ${user.token}` } };
        const { data } = await axios.get(`${API_URL}/tasks/my`, config);
        setTasks(data);
      } catch (err) {
        console.error("Error fetching tasks for dashboard:", err);
      } finally {
        setLoadingTasks(false);
      }

      // Fetch Events
      try {
        setLoadingEvents(true);
        const config = { headers: { Authorization: `Bearer ${user.token}` } };
        const { data } = await axios.get(`${API_URL}/events`, config);
        setEvents(data);
      } catch (err) {
        console.error("Error fetching events for dashboard:", err);
      } finally {
        setLoadingEvents(false);
      }
    };

    fetchDashboardData();
  }, [user, API_URL]);

  const handleToggleTask = async (taskId) => {
    try {
      // Optimistic update
      setTasks(prev => prev.map(t => t._id === taskId ? { ...t, status: 'done', isDoneChanging: true } : t));
      
      const config = { headers: { Authorization: `Bearer ${user.token}` } };
      await axios.put(`${API_URL}/tasks/${taskId}`, { status: 'done' }, config);
      
      // Allow anim to complete before removing
      setTimeout(() => {
        setTasks(prev => prev.filter(t => t._id !== taskId));
      }, 700);
    } catch (err) {
      console.error("Error updating task status:", err);
      // Revert state
      try {
        const config = { headers: { Authorization: `Bearer ${user.token}` } };
        const { data } = await axios.get(`${API_URL}/tasks/my`, config);
        setTasks(data);
      } catch (e) {
        console.error(e);
      }
    }
  };

  if (!profile) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] animate-fade-in">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-primary/20 rounded-full"></div>
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
        </div>
        <p className="mt-6 text-title-medium text-surface-onVariant font-medium animate-pulse">Lade Dashboard...</p>
      </div>
    );
  }

  const handleResendVerification = async () => {
    try {
      setResendLoading(true);
      setResendMessage('');
      await axios.post(`${API_URL}/auth/resend-verification`, { email: profile.email });
      setResendMessage('E-Mail erfolgreich gesendet!');
    } catch (error) {
      console.error(error);
      setResendMessage('Fehler beim Senden.');
    } finally {
      setResendLoading(false);
    }
  };

  const getCategorizedLinks = () => {
    const role = profile.role;
    const isApproved = profile.isApproved;
    const groups = [
      { id: 'personal', title: 'Mein Bereich', links: [] },
      { id: 'family', title: 'Familie', links: [] },
      { id: 'content', title: 'Inhalte & Kommunikation', links: [] },
      { id: 'admin', title: 'Verwaltung', links: [] },
      { id: 'pfadi', title: 'Pfadi-Alltag', links: [] }
    ];

    const findGroup = (id) => groups.find(g => g.id === id);

    findGroup('personal').links.push({
      to: 'profile',
      icon: User,
      title: 'Mein Account',
      description: 'Zentrale Kontoeinstellungen',
      colorClass: 'primary'
    });

    findGroup('personal').links.push({
      to: 'inbox',
      icon: InboxIcon,
      title: 'Postfach',
      description: 'Benachrichtigungen & Aktionen',
      colorClass: 'tertiary'
    });

    findGroup('personal').links.push({
      to: 'notifications',
      icon: Bell,
      title: 'Benachrichtigungen',
      description: 'Einstellungen anpassen',
      colorClass: 'secondary'
    });

    if (role === 'eltern') {
      findGroup('family').links.push({
        to: 'my-children',
        icon: Users,
        title: 'Meine Kinder',
        description: 'Kinder verwalten & anmelden',
        colorClass: 'tertiary'
      });
    }

    if (isApproved && (role === 'admin' || role === 'gruppenleiter')) {
      findGroup('content').links.push({
        to: 'my-team-page',
        icon: Globe,
        title: 'Meine Seite',
        description: 'Öffentliches Profil verwalten',
        colorClass: 'primary'
      });
      findGroup('content').links.push({
        to: 'blog',
        icon: FileText,
        title: 'Blog',
        description: 'Beiträge verwalten & freigeben',
        colorClass: 'tertiary'
      });
      findGroup('content').links.push({
        to: 'contact-requests',
        icon: MessageSquare,
        title: 'Kontaktanfragen',
        description: 'Eingehende Nachrichten',
        colorClass: 'secondary'
      });
    } else if (role === 'kind') {
      findGroup('content').links.push({
        to: 'blog',
        icon: FileText,
        title: 'Blog schreiben',
        description: 'Eigene Beiträge als Entwurf erstellen',
        colorClass: 'tertiary'
      });
    }

    if (isApproved && (role === 'admin' || role === 'gruppenleiter')) {
      findGroup('admin').links.push({
        to: 'events',
        icon: Calendar,
        title: 'Events',
        description: 'Veranstaltungen verwalten',
        colorClass: 'primary'
      });
      findGroup('admin').links.push({
        to: 'tasks',
        icon: TaskIcon,
        title: 'Aufgaben',
        description: 'Aufgaben- & Kanban-Board verwalten',
        colorClass: 'secondary'
      });
    }
    if (isApproved && role === 'admin') {
      findGroup('admin').links.push({
        to: 'pending-registrations',
        icon: UserPlus,
        title: 'Registrierungen',
        description: 'Neue Anfragen prüfen',
        colorClass: 'tertiary'
      });
      findGroup('admin').links.push({
        to: 'users',
        icon: Users,
        title: 'Benutzer',
        description: 'Mitglieder verwalten',
        colorClass: 'secondary'
      });
      findGroup('admin').links.push({
        to: 'admin-tools',
        icon: Settings,
        title: 'Admin-Tools',
        description: 'System-Einstellungen',
        colorClass: 'error'
      });
    }

    findGroup('pfadi').links.push({
      to: 'my-registrations',
      icon: ClipboardList,
      title: 'Meine Anmeldungen',
      description: 'Anmeldestatus prüfen',
      colorClass: 'secondary'
    });

    return groups.filter(g => g.links.length > 0);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcomingEvents = events
    .filter(e => {
      const eventDate = new Date(e.date);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate >= today && e.status === 'published';
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  
  // Get all events on the next date
  let nextEvents = [];
  if (upcomingEvents.length > 0) {
    const nextDate = new Date(upcomingEvents[0].date).toDateString();
    nextEvents = upcomingEvents.filter(e => new Date(e.date).toDateString() === nextDate);
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 md:py-12 animate-fade-in selection:bg-primary/20">
      {showTutorial && (
        <TutorialModal pageId="dashboard" onClose={() => setShowTutorial(false)} />
      )}
      
      <header className="mb-6 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-display-small md:text-display-medium font-bold text-surface-on mb-3 leading-tight tracking-tight">
            Willkommen, {profile.userData?.scoutName || profile.userData?.firstName || 'Pfadfinder'}!
          </h1>
          <div className="flex items-center gap-3">
            <span className="text-body-large text-surface-onVariant font-medium opacity-80">
              {orgSettings?.dashboardWelcomeSubtitle || 'Eingeloggt als'}
            </span>
            <span className="md3-chip-flat !bg-primary/10 !text-primary font-bold uppercase tracking-wider text-[10px] px-3 py-1 rounded-full">
              {getRoleLabel(profile.role)}
            </span>
          </div>
        </div>
      </header>

      {/* Quick Actions dock */}
      <QuickActionsDock role={profile.role} isApproved={profile.isApproved} />

      {/* Setup Checklist (admin only – shows when required keys are missing) */}
      {profile.role === 'admin' && (
        <SetupChecklistBanner user={user} API_URL={API_URL} />
      )}

      {/* Consolidated notifications alert widget */}
      <SystemAlertsWidget 
        profile={profile} 
        resendLoading={resendLoading} 
        resendMessage={resendMessage} 
        handleResendVerification={handleResendVerification} 
      />

      {/* Responsive Two-Column Dashboard Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left Column (Next Event, Pulse, Links) */}
        <div className="lg:col-span-2 space-y-10">
          
          {/* Next Events Widget */}
          {nextEvents.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 pl-2">
                <Calendar size={18} className="text-primary" />
                <h2 className="text-title-medium font-bold text-surface-on uppercase tracking-widest text-xs">
                  {nextEvents.length > 1 ? 'Nächste Ereignisse (Parallel)' : 'Nächstes Ereignis'}
                </h2>
              </div>
              <NextEventsWidget events={nextEvents} loading={loadingEvents} />
            </div>
          )}

          {/* Statistics Pulse */}
          <DashboardPulse profile={profile} API_URL={API_URL} />

          {/* Categorized Quick Links */}
          <div className="space-y-12">
            {getCategorizedLinks().map((group) => (
              <section key={group.id} className="animate-fade-in-up">
                <div className="flex items-center gap-6 mb-6">
                  <h2 className="text-headline-small font-bold text-surface-on shrink-0 tracking-tight">
                    {group.title}
                  </h2>
                  <div className="h-0.5 bg-outlineVariant flex-1 opacity-20 rounded-full" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {group.links.map((link, index) => (
                    <QuickLinkCard key={index} {...link} />
                  ))}
                </div>
              </section>
            ))}
          </div>

        </div>

        {/* Right Column (Tasks list) */}
        <div className="lg:col-span-1 space-y-8">
          
          {/* Interactive task checklist widget */}
          {(profile.role === 'admin' || profile.role === 'gruppenleiter') && (
            <TaskListWidget 
              tasks={tasks} 
              loading={loadingTasks} 
              onToggleTask={handleToggleTask} 
              navigate={navigate} 
            />
          )}

        </div>

      </div>
    </div >
  );
};

export default Dashboard;
